import { RSU } from '_lib/api/types';
import { RootState, useAppDispatch } from '_store';
import applicationReducer from '_store/application/reducer';
import { DialogType } from '_store/application/types';
import { BlockedActionMode } from '_lib/auth/rbac';
import { getRsuStatusFromDiagnostic } from '_store/devices/utils';
import { useLocalization } from '@fluent/react';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Card, CardContent, Tooltip } from '@material-ui/core';
import { blue, red } from '@material-ui/core/colors';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';

import { RsuStatus } from '../../../icons';
import { Accordion } from '../../../items/Accordion';
import { RoleGuard } from '../../../items/RoleGuard';
import { toLatLng } from '../../../routes/MapView/utils';
import CardDetails from './Details';
import styles from './styles';

export interface CardListItemProps {
  text: string;
  device: RSU;
  asCard: boolean;
  onUpdate?: (idExpanded: string) => void;
  onDelete?: (id: string) => void;
  writeAllowed?: boolean;
  blockedActionMode?: BlockedActionMode;
  blockedMessage?: string;
}

const CardListItem: FC<CardListItemProps> = ({
  text,
  device,
  onUpdate,
  onDelete,
  asCard,
  writeAllowed = true,
  blockedActionMode = 'disable',
  blockedMessage = '',
}) => {
  const { l10n } = useLocalization();
  const classes = styles();
  const dispatch = useAppDispatch();
  const diagnostic = useSelector((state: RootState) => state.api.diagnostic[device.id]);
  const mapRef = useSelector((state: RootState) => state.application.mapRef);

  const onAccordionExpanded = () => {
    const markerPosition = toLatLng(device?.location?.coordinates);
    mapRef.flyTo(markerPosition, 15, { animate: true, duration: 1 });

    setTimeout(() => {
      dispatch(
        applicationReducer.actions.infoSelected({
          type: DialogType.DEVICE_PATH,
          id: device.id,
        }),
      );
    }, 1000);
  };

  const onAccordionClose = () => {
    dispatch(
      applicationReducer.actions.infoSelected({
        type: DialogType.DEVICE,
        id: '4',
      }),
    );
  };

  const iconStatus = getRsuStatusFromDiagnostic(diagnostic);

  const statusExists = Boolean(diagnostic && diagnostic.statusDetails);

  const details = (
    <CardDetails
      device={device}
      diagnostic={{
        connection: statusExists,
        service: statusExists,
        statusDetails: diagnostic?.statusDetails,
        updatedAt: diagnostic?.updatedAt,
      }}
    />
  );

  const body = (
    <CardContent className={classes.cardContent}>
      <Box className={classes.headerBox}>
        <div className={classes.headerIconAndTitle}>
          <RsuStatus status={iconStatus} />
          <span>{text}</span>
        </div>
        <div>
          <RoleGuard
            allowed={writeAllowed}
            blockedActionMode={blockedActionMode}
            blockedMessage={blockedMessage}
          >
            <Tooltip title={l10n.getString('forms-event-update-btn')}>
              <FontAwesomeIcon
                icon={faPen}
                style={{
                  backgroundColor: blue[400],
                }}
                className={classes.iconButton}
                size="xs"
                onClick={() => onUpdate?.(device.id)}
              />
            </Tooltip>
          </RoleGuard>
          <RoleGuard
            allowed={writeAllowed}
            blockedActionMode={blockedActionMode}
            blockedMessage={blockedMessage}
          >
            <Tooltip title={l10n.getString('forms-event-delete-btn')}>
              <FontAwesomeIcon
                icon={faTrash}
                style={{
                  backgroundColor: red[400],
                }}
                className={classes.iconButton}
                size="xs"
                onClick={() => onDelete?.(device.id)}
              />
            </Tooltip>
          </RoleGuard>
        </div>
      </Box>

      {asCard ? (
        <>
          <Accordion
            title={l10n.getString('view-details')}
            onExpanded={onAccordionExpanded}
            onClose={onAccordionClose}
          >
            {details}
          </Accordion>
        </>
      ) : (
        details
      )}
    </CardContent>
  );

  if (asCard) {
    return <Card className={classes.root}>{body}</Card>;
  }
  return body;
};

export default React.memo(CardListItem);
